import { useAppContext } from "@contexts";
import { Menu, MenuGroup } from "@controls";
import {
  ArrowLeftRight,
  Building2,
  CircleUser,
  LogOut,
  ReceiptText,
} from "@icons";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { useOrganizationContext } from "@src/modules/member/context/organization-context/hook";
import { useLogout, useMe } from "@src/modules/member/hooks/me";
import {
  useOrganization,
  useOrganizations,
} from "@src/modules/member/hooks/organizations";
import { useMemberPath } from "@src/modules/member/hooks/use-member-path";
import { useHasPermission } from "@src/modules/member/hooks/use-permissions";
import type { Organization } from "@src/modules/member/models/organization";
import {
  INVOICE_SETTINGS_PATH,
  NEW_ORGANIZATION_PATH,
  ORGANIZATION_PATH,
  PROFILE_PATH,
} from "@src/modules/member/modules/settings/router/paths";
import { toMemberPath } from "@src/modules/member/path";
import classNames from "classnames";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function OrganizationImage({ organization }: { organization?: Organization }) {
  if (!organization?.name) {
    return null;
  }

  if (organization.image_url) {
    return (
      <img
        src={organization.image_url}
        alt={organization.name}
        className="w-10 rounded"
      />
    );
  }

  return <div>{organization.name[0].toUpperCase()}</div>;
}

interface OrganizationProps {
  minimized: boolean;
}

function Organization({ minimized }: OrganizationProps) {
  const { t } = useTranslation();

  const { setSwitchOrganizationDialogIsOpen } = useAppContext();
  const { currentOrganizationCode } = useOrganizationContext();
  const { settingsPath } = useMemberPath();
  const navigate = useNavigate();
  const {
    data: organizationsResponse,
    isLoading: organizationsIsLoading,
    dataUpdatedAt: organizationsUpdatedAt,
  } = useOrganizations();
  const { data: organizationResponse } = useOrganization(
    currentOrganizationCode as string,
  );

  useEffect(() => {
    if (!organizationsIsLoading && organizationsResponse?.data?.length === 0) {
      navigate(toMemberPath(NEW_ORGANIZATION_PATH()));
    }
  }, [organizationsUpdatedAt]);

  const { mutate: logout } = useLogout();

  const { data: meResponse, isLoading: meIsLoading } = useMe();
  const canUpdateOrg = useHasPermission(PERMISSIONS.ORGANIZATION_UPDATE);
  const canViewInvoices = useHasPermission(PERMISSIONS.INVOICES_VIEW_LIST);

  const me = meResponse?.data ?? { email: "", first_name: "", last_name: "" };

  const organization = organizationResponse?.data;

  const cantDisplayOrganizations = !organization || meIsLoading;
  if (cantDisplayOrganizations) {
    return (
      <div
        className={classNames(
          "border-gray-200 bg-white rounded-lg w-full h-fit p-2 flex gap-2",
        )}
      >
        <div className="h-10 w-10 bg-gray-200 rounded" />
        {!minimized && (
          <div className="flex-grow flex flex-col gap-1">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-[12px] w-2/3 bg-gray-200 rounded" />
          </div>
        )}
      </div>
    );
  }

  const handleLogout = () => {
    logout(null);
  };

  const memberName = [me.first_name, me.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  const groups: MenuGroup[] = [
    {
      key: "settings",
      label: t("settings"),
      items: [
        ...(canUpdateOrg
          ? [
              {
                key: "organization",
                icon: <Building2 />,
                label: t("organizationSettings"),
                onSelect: () => navigate(settingsPath(ORGANIZATION_PATH())),
              },
            ]
          : []),
        {
          key: "profile",
          icon: <CircleUser />,
          label: t("profileSettings"),
          onSelect: () => navigate(settingsPath(PROFILE_PATH())),
        },
        ...(canViewInvoices
          ? [
              {
                key: "invoiceSettings",
                icon: <ReceiptText />,
                label: t("member.settings.invoices.invoiceSettings"),
                onSelect: () => navigate(settingsPath(INVOICE_SETTINGS_PATH())),
              },
            ]
          : []),
      ],
    },
    {
      key: "organizationActions",
      items: [
        {
          key: "switchOrganization",
          icon: <ArrowLeftRight />,
          label: t("switchOrganization"),
          onSelect: () => setSwitchOrganizationDialogIsOpen(true),
        },
      ],
    },
    {
      key: "session",
      items: [
        {
          key: "logout",
          icon: <LogOut />,
          label: t("logout"),
          onSelect: handleLogout,
        },
      ],
    },
  ];

  return (
    <Menu
      ariaLabel={t("accountMenu")}
      header={{
        name: memberName || organization.name,
        email: me.email,
      }}
      trigger={
        <button
          type="button"
          className={classNames(
            "border-gray-200 bg-white rounded-lg",
            "p-2 flex text-start",
            "w-full h-fit",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-primary-300",
          )}
        >
          <div className="h-10 min-w-10 bg-white rounded flex justify-center items-center text-lg font-bold">
            <OrganizationImage organization={organization} />
          </div>
          {!minimized && (
            <div className="text-gray-900 flex flex-grow flex-col justify-center min-w-0 max-w-full text-start ml-2">
              <div className="text-sm overflow-hidden whitespace-nowrap font-semibold my-0">
                {organization.name}
              </div>
              <span className="my-0 font-light text-xs">{me.email}</span>
            </div>
          )}
        </button>
      }
      groups={groups}
    />
  );
}

export default Organization;
